import React from "react";
import { Navigate, useLocation, useOutlet } from "react-router-dom";
import { localUrlEnum } from "../Utils/UrlEnum";

const PrivateRoute = (props: { children: React.ReactNode }): JSX.Element => {
  const location = useLocation();
  const outlet: any = useOutlet();
  const additionalProp: any = outlet?.props?.children?.props?.children?.props;

  /**
   *
   * @returns
   */
  const isLoggedIn = () => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      return false;
    }
    return true;
  };

  /**
   * Check if user has access to the current route based on their role's allowedRoutes
   * @returns boolean
   */
  const hasAccessToRoute = () => {
    const userRolesJson = localStorage.getItem("userRoles");
    if (!userRolesJson) return false;

    const userRoles = JSON.parse(userRolesJson);

    // If user has any system role (isSystem: true), grant access to all routes
    const hasSystemRole = userRoles.some((role: any) => role.isSystem === true);
    if (hasSystemRole) return true;

    const allAllowedRoutes = userRoles.flatMap((role: any) => role.allowedRoutes || []);

    // Get the current path without query params
    const currentPath = location.pathname;

    // Always allow root path (will redirect to dashboard)
    if (currentPath === '/' || currentPath === '') return true;

    // Check if current path matches any allowed route
    // Parent route grants access to all child routes (wildcard matching)
    return allAllowedRoutes.some((allowedRoute: string) => {
      // Remove query parameter indicators (?) and trailing slashes for comparison
      const normalizedAllowed = allowedRoute.replace(/\?$/, '').replace(/\/$/, '');
      const normalizedCurrent = currentPath.replace(/\/$/, '');

      // Exact match: /dashboard === /dashboard
      if (normalizedAllowed === normalizedCurrent) return true;

      // Parent route wildcard: /whatsApp allows /whatsApp/* (everything under it)
      // This allows /whatsApp/phoneNumbers, /whatsApp/templates, /whatsApp/template/123, etc.
      if (normalizedCurrent.startsWith(normalizedAllowed + '/')) return true;

      // Also check with query parameters: /orders allows /orders?page=1
      if (normalizedCurrent.startsWith(normalizedAllowed)) return true;

      return false;
    });
  };

  /**
   *
   */
  const renderChildrenWithProps: any = () => {
    return React.Children.map(props.children, (child) => {
      if (React.isValidElement(child)) {
        // Clone the child element with additional props
        return React.cloneElement(child, additionalProp);
      }
      return child;
    });
  };

  // Check if user is logged in
  if (!isLoggedIn()) {
    return (
      <Navigate
        replace={true}
        to={localUrlEnum.login}
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  // Check if user has access to this route
  if (!hasAccessToRoute()) {
    // Redirect to dashboard if user doesn't have access to this route
    return <Navigate replace={true} to={localUrlEnum.dashboard} />;
  }

  return renderChildrenWithProps();
};

export default PrivateRoute;
